import {
  adminFoodDetailSchema,
  adminFoodListItemSchema,
  adminFoodResearchDraftSchema,
  type AdminFoodCreateRequest,
  type AdminFoodDetail,
  type AdminFoodListItem,
  type AdminFoodResearchDraft,
  type AdminFoodResearchRequest,
  type AdminFoodRecipeUpdateRequest,
  type AdminFoodUpdateRequest,
  type FoodDietClassName,
} from '@steado/admin-contract/foods';
import { paginationMetaSchema, type PaginationMeta } from '@steado/admin-contract/pagination';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';

import type { ApiClient, QueryValue } from '@/lib/api/client';

export interface FoodsFilters {
  readonly search?: string | undefined;
  readonly kind?: string | undefined;
  readonly source?: string | undefined;
  readonly dietClass?: string | undefined;
  readonly mealRole?: string | undefined;
  readonly prepTimeBand?: string | undefined;
  readonly costBand?: string | undefined;
  readonly verification?: string | undefined;
  readonly activity?: string | undefined;
  /** 'without' is the curation queue: foods a portion would make plannable. */
  readonly householdPortion?: string | undefined;
  readonly order?: string | undefined;
  readonly cursor?: string | undefined;
}

export interface FoodsPage {
  readonly items: AdminFoodListItem[];
  readonly pagination: PaginationMeta;
}

const foodsPageSchema = z.object({
  items: z.array(adminFoodListItemSchema),
  pagination: paginationMetaSchema,
});

export const FOODS_PAGE_SIZE = 20;

export function foodsQueryOptions(api: ApiClient, filters: FoodsFilters) {
  const query: Record<string, QueryValue> = {
    search: filters.search,
    kind: filters.kind,
    source: filters.source,
    dietClass: filters.dietClass,
    mealRole: filters.mealRole,
    prepTimeBand: filters.prepTimeBand,
    costBand: filters.costBand,
    verification: filters.verification,
    activity: filters.activity,
    householdPortion: filters.householdPortion,
    order: filters.order,
    cursor: filters.cursor,
    limit: FOODS_PAGE_SIZE,
  };

  return queryOptions({
    queryKey: ['admin', 'foods', filters],
    queryFn: async (): Promise<FoodsPage> => {
      const response = await api.requestWithMeta({
        path: '/admin/foods',
        query,
        schema: z.array(adminFoodListItemSchema),
      });

      return foodsPageSchema.parse({ items: response.data, pagination: response.meta.pagination });
    },
    placeholderData: (previous) => previous,
  });
}

export function foodQueryOptions(api: ApiClient, foodId: string | null) {
  return queryOptions({
    queryKey: ['admin', 'food', foodId],
    queryFn: (): Promise<AdminFoodDetail> =>
      api.request({ path: `/admin/foods/${foodId ?? ''}`, schema: adminFoodDetailSchema }),
    enabled: foodId !== null,
  });
}

/**
 * Food-level mutations. Each returns the food the operator should now be
 * looking at — the edited row, or for a merge, the survivor — so the drawer
 * is written straight from the response rather than refetched.
 */
/**
 * A whole new catalog row. Returns the food to open the drawer on, the same
 * write-then-read-the-detail shape every other mutation here uses.
 */
export function createFood(api: ApiClient, body: AdminFoodCreateRequest): Promise<AdminFoodDetail> {
  return api.request({
    path: '/admin/foods',
    method: 'POST',
    body,
    schema: adminFoodDetailSchema,
  });
}

/**
 * Drafts a food with the model. Writes nothing — the draft fills the form, and
 * the form is what creates the row.
 */
export function researchFood(
  api: ApiClient,
  body: AdminFoodResearchRequest,
): Promise<AdminFoodResearchDraft> {
  return api.request({
    path: '/admin/foods/research',
    method: 'POST',
    body,
    schema: adminFoodResearchDraftSchema,
  });
}

/** [dietClass] is the operator's confirmed class: required for a photo-scan estimate. */
export function verifyFood(
  api: ApiClient,
  foodId: string,
  jainExcluded: boolean,
  dietClass?: FoodDietClassName,
): Promise<AdminFoodDetail> {
  return api.request({
    path: `/admin/foods/${foodId}/verify`,
    method: 'POST',
    body: dietClass === undefined ? { jainExcluded } : { jainExcluded, dietClass },
    schema: adminFoodDetailSchema,
  });
}

export function deactivateFood(api: ApiClient, foodId: string, reason: string): Promise<AdminFoodDetail> {
  return api.request({
    path: `/admin/foods/${foodId}/deactivate`,
    method: 'POST',
    body: { reason },
    schema: adminFoodDetailSchema,
  });
}

export function reactivateFood(api: ApiClient, foodId: string): Promise<AdminFoodDetail> {
  return api.request({
    path: `/admin/foods/${foodId}/reactivate`,
    method: 'POST',
    schema: adminFoodDetailSchema,
  });
}

export function mergeFood(api: ApiClient, foodId: string, intoFoodId: string): Promise<AdminFoodDetail> {
  return api.request({
    path: `/admin/foods/${foodId}/merge`,
    method: 'POST',
    body: { intoFoodId },
    schema: adminFoodDetailSchema,
  });
}

export function updateFood(
  api: ApiClient,
  foodId: string,
  body: AdminFoodUpdateRequest,
): Promise<AdminFoodDetail> {
  return api.request({
    path: `/admin/foods/${foodId}`,
    method: 'PATCH',
    body,
    schema: adminFoodDetailSchema,
  });
}

/**
 * Replaces a dish's recipe, minting a new MANUAL version and re-deriving the
 * dish from it.
 */
export function updateFoodRecipe(
  api: ApiClient,
  foodId: string,
  body: AdminFoodRecipeUpdateRequest,
): Promise<AdminFoodDetail> {
  return api.request({
    path: `/admin/foods/${foodId}/recipe`,
    method: 'PUT',
    body,
    schema: adminFoodDetailSchema,
  });
}

/** Re-derives a dish from its current recipe, without editing it. */
export function deriveFoodRecipe(api: ApiClient, foodId: string): Promise<AdminFoodDetail> {
  return api.request({
    path: `/admin/foods/${foodId}/recipe/derive`,
    method: 'POST',
    schema: adminFoodDetailSchema,
  });
}
